/**
 * FN-FE-SUB-01 + FN-FIX-B1 — Step 4: Csomag-választás
 */
import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { post } from '../../lib/api';
import Button from '../../components/ui/Button';
import { StepDots } from './OnboardingCompanyStep';
import { useToast } from '../../components/ui/Toast';

const PLANS = {
  start: { code: 'start', name: 'Start', priceMonthly: 299000, priceYearly: 2870400, subtitle: 'A számlázó alap — kisvállalkozóknak.', features: ['Partnerek', 'Cikkek + Árlisták', 'Számlák + NAV', 'Bejövő számlák', 'Kintlévőség', 'Dokumentumtár'], seats: '1 felhasználó' },
  standard: { code: 'standard', name: 'Standard', priceMonthly: 599000, priceYearly: 5750400, subtitle: 'A teljes vállalatkezelés — kkv-knak.', features: ['Minden Start funkció', '+ Megrendelések + Szállítólevelek', '+ Projektek + Feladatok', '5 ismétlődő számla/hó', '2 felhasználó + 1 könyvelő'], seats: '2+1 felhasználó' },
  pro: { code: 'pro', name: 'Pro', priceMonthly: 1299000, priceYearly: 12470400, subtitle: 'A szervezeti eszköz — növekvő cégeknek.', features: ['Minden Standard funkció', '+ Beszerzés', '+ Időnaplózás', '+ Költségallokáció', '100 ismétlődő számla/hó', '3+1+1 felhasználó'], seats: '3+1+1 felhasználó' },
} as const;

function formatHuf(filler: number): string {
  return Math.round(filler / 100).toLocaleString('hu-HU');
}

export default function OnboardingPlanStep() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const { toast } = useToast();
  const [selectedPlan, setSelectedPlan] = useState<'start' | 'standard' | 'pro'>('standard');
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');
  const [loading, setLoading] = useState(false);

  const planPrice = useMemo(() => {
    const plan = PLANS[selectedPlan];
    return billingCycle === 'yearly' ? Math.round(plan.priceYearly / 12) : plan.priceMonthly;
  }, [selectedPlan, billingCycle]);

  async function handleContinue() {
    setLoading(true);
    try {
      await post('/setup/plan', { plan: selectedPlan, billingCycle });
      navigate('/onboarding/confirm');
    } catch (err: any) { toast(err.message || 'Hiba történt.', 'error'); } finally { setLoading(false); }
  }

  return (
    <div className="onb-bg">
      <div style={{ maxWidth: '860px', margin: '0 auto' }}>
        <StepDots current={4} total={5} />
        <h1 className="onb-title">Válaszd ki a csomagodat</h1>
        <p className="onb-sub">30 napos ingyenes próba — bármikor váltasz, nincs elköteleződés.</p>

        {/* Billing toggle */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', marginBottom: '24px' }}>
          <span style={{ fontWeight: billingCycle === 'monthly' ? 700 : 400, fontSize: '14px', color: billingCycle === 'monthly' ? 'var(--navy)' : 'var(--slate)' }}>Havi</span>
          <button
            onClick={() => setBillingCycle(c => c === 'monthly' ? 'yearly' : 'monthly')}
            style={{ width: '44px', height: '24px', borderRadius: '12px', background: billingCycle === 'yearly' ? 'var(--orange)' : 'var(--line)', border: 'none', cursor: 'pointer', position: 'relative', transition: 'background .2s' }}
          >
            <div style={{ width: '18px', height: '18px', borderRadius: '50%', background: '#fff', position: 'absolute', top: '3px', left: billingCycle === 'yearly' ? '23px' : '3px', transition: 'left .2s', boxShadow: '0 1px 3px rgba(0,0,0,.2)' }} />
          </button>
          <span style={{ fontWeight: billingCycle === 'yearly' ? 700 : 400, fontSize: '14px', color: billingCycle === 'yearly' ? 'var(--navy)' : 'var(--slate)' }}>
            Éves <span style={{ color: 'var(--orange)', fontWeight: 700 }}>(-20%)</span>
          </span>
        </div>

        {/* Plan cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '14px', marginBottom: '24px' }}>
          {(['start', 'standard', 'pro'] as const).map(planKey => {
            const plan = PLANS[planKey];
            const isSelected = selectedPlan === planKey;
            const isPopular = planKey === 'standard';
            const price = billingCycle === 'yearly' ? Math.round(plan.priceYearly / 12) : plan.priceMonthly;

            return (
              <div
                key={planKey}
                onClick={() => setSelectedPlan(planKey)}
                style={{
                  position: 'relative', padding: '24px', borderRadius: '14px', cursor: 'pointer',
                  border: isSelected ? '2px solid var(--orange)' : '1px solid var(--line)',
                  background: isSelected ? 'var(--orange-soft)' : 'var(--paper)',
                  boxShadow: isSelected ? '0 4px 16px rgba(244,123,32,.15)' : 'none',
                  transition: 'border .15s, background .15s, box-shadow .15s',
                }}
              >
                {isPopular && (
                  <div style={{ position: 'absolute', top: '-10px', left: '50%', transform: 'translateX(-50%)', background: 'var(--orange)', color: '#fff', fontSize: '10px', fontWeight: 800, padding: '3px 12px', borderRadius: '20px', letterSpacing: '.06em', textTransform: 'uppercase', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                    Népszerű
                  </div>
                )}
                <h3 style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: '20px', fontWeight: 800, color: 'var(--navy)', marginBottom: '4px' }}>{plan.name}</h3>
                <div style={{ marginBottom: '4px' }}>
                  <span style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: '28px', fontWeight: 800, color: 'var(--navy)' }}>{formatHuf(price)}</span>
                  <span style={{ fontSize: '13px', color: 'var(--slate)', marginLeft: '4px' }}>Ft / hó</span>
                </div>
                {billingCycle === 'yearly' && (
                  <div style={{ fontSize: '12px', color: 'var(--orange)', fontWeight: 600, marginBottom: '6px' }}>Éves: {formatHuf(plan.priceYearly)} Ft (-20%)</div>
                )}
                <div style={{ fontSize: '12.5px', color: 'var(--slate)', marginBottom: '12px' }}>{plan.subtitle}</div>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {plan.features.map(f => (
                    <li key={f} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', fontSize: '13px', marginBottom: '6px', color: 'var(--navy)' }}>
                      <span style={{ color: 'var(--green)', flexShrink: 0 }}>✓</span>{f}
                    </li>
                  ))}
                </ul>
                <div style={{ fontSize: '11px', color: 'var(--slate-2)', marginTop: '10px' }}>{plan.seats}</div>
              </div>
            );
          })}
        </div>

        {/* Summary + CTA */}
        <div className="onb-card">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: '14px', color: 'var(--navy)' }}>{PLANS[selectedPlan].name} csomag</div>
              <div style={{ fontSize: '12px', color: 'var(--slate)' }}>{billingCycle === 'yearly' ? 'Éves számlázás' : 'Havi számlázás'}</div>
            </div>
            <div style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: '22px', fontWeight: 800, color: 'var(--navy)' }}>
              {formatHuf(planPrice)} Ft/hó
            </div>
          </div>

          <div style={{ background: 'var(--orange-soft)', padding: '12px 14px', borderRadius: '10px', marginBottom: '14px' }}>
            <div style={{ fontWeight: 700, fontSize: '13px', color: 'var(--orange-deep)' }}>Most ingyen — 30 napig.</div>
            <div style={{ fontSize: '12px', color: 'var(--slate)' }}>A 15. napon 10% kedvezmény az első fizetésnél.</div>
          </div>

          <div className="onb-footer" style={{ justifyContent: 'space-between' }}>
            <Button variant="ghost" onClick={() => navigate('/onboarding/nav')}>← Vissza</Button>
            <Button loading={loading} onClick={handleContinue}>Tovább a megerősítéshez →</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
